/**
 * Sign-in for any of the three apps (EP10-S01).
 *
 * Each app mounts this once, naming itself. The name is what the API client
 * sends as `X-Khazzin-Interface` on every request — `/me/context` included — so
 * the server knows which of the three shells is asking, and every audit row
 * records it.
 *
 * AuthKit itself is the same in all three: the same WorkOS application, the
 * same token, a different redirect URI per host. The Admin Console still mounts
 * `AuthKitProvider` directly and its client defaults to `admin`, which keeps its
 * requests exactly what they were before the other two apps existed.
 */
import type { Interface } from "@khazzin/api-client";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { type ComponentProps, createContext, type ReactNode, useContext } from "react";

const InterfaceContext = createContext<Interface | null>(null);

type AuthKitProps = ComponentProps<typeof AuthKitProvider>;

export interface AuthProviderProps {
	/** Which app this is. */
	interface: Interface;
	/** The WorkOS client id. Public by design. */
	clientId: string;
	/** Must match a redirect URI registered for this app's host in WorkOS. */
	redirectUri: string;
	/**
	 * The custom WorkOS authentication domain, when there is one. It is what
	 * keeps the session cookie first-party, so a reload stays signed in.
	 */
	apiHostname?: string | undefined;
	onRedirectCallback?: AuthKitProps["onRedirectCallback"];
	children: ReactNode;
}

export function AuthProvider({
	interface: caller,
	clientId,
	redirectUri,
	apiHostname,
	onRedirectCallback,
	children,
}: AuthProviderProps) {
	return (
		<AuthKitProvider
			clientId={clientId}
			redirectUri={redirectUri}
			{...(apiHostname ? { apiHostname } : {})}
			{...(onRedirectCallback === undefined ? {} : { onRedirectCallback })}
		>
			<InterfaceContext.Provider value={caller}>{children}</InterfaceContext.Provider>
		</AuthKitProvider>
	);
}

/**
 * The app's interface name. Throws outside {@link AuthProvider}: an app that
 * forgot to say what it is would otherwise be recorded as somebody else.
 */
export function useInterface(): Interface {
	const caller = useContext(InterfaceContext);
	if (caller === null) {
		throw new Error("useInterface must be used inside <AuthProvider>");
	}
	return caller;
}

export { AuthKitProvider };
